import { OrderDetailContent } from '@euler/app/flows/order/components/OrderDetailContent';
import { TaskTimeline } from '@euler/app/flows/task/components/TaskTimeline';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { ChildTaskList } from '@euler/app/flows/task/pages/detail/components/ChildTaskList';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { HStack } from '@euler/components/layout/Stack';
import { useStoreFeature } from '@euler/functions/entitlements/useStoreFeature';
import {
  ResponsiveContextProvider,
  useResponsiveContext,
} from '@euler/functions/responsive/ResponsiveContext';
import { PixelScaleFn } from '@euler/functions/responsive/utils';
import {
  CommonTaskStatus,
  InspectionTaskStatus,
  StoreFeature,
} from '@euler/model/enum';
import { safeMarkDevFlag } from '@euler/utils';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { memo, useCallback, useRef } from 'react';
import {
  Platform,
  RefreshControl as RNRefreshControl,
  ScrollViewProps,
  View,
} from 'react-native';
import { ScrollView, createNativeWrapper } from 'react-native-gesture-handler';
import { SharedValue } from 'react-native-reanimated';
import { WiredConstructionInfo } from './ConstructionInfo';
import { WiredTaskDetailPageOrderSummary } from './Content.shared';
import { WiredDeliveryCheckInfo } from './DeliveryCheckInfo';
import { WiredFaultInspectionInfo } from './FaultInspectionInfo';
import { WiredTaskDetailFinishAction } from './FinishAction';
import { WiredInspectionInfo } from './InspectionInfo';
import { TaskDetailMenu } from './Menu';
import { WiredPreInspectionInfo } from './PreInspectionInfo';
import { TaskDetailSection } from './TaskDetailSection';
import { WiredRemarkSection } from './WiredRemarkSection';

const kAlwaysShowPreInspectionInfo = safeMarkDevFlag(false);

enum TabletRightColumnContentType {
  Timeline = 'timeline',
  OrderDetail = 'order-detail',
}

const RefreshControl =
  Platform.OS === 'android'
    ? createNativeWrapper(RNRefreshControl, {
        disallowInterruption: true,
        shouldCancelWhenOutside: false,
      })
    : RNRefreshControl;

export const TaskDetailPageContentForIpad = memo(
  ({
    serviceRecordCount,
    scrollY,
    onRefresh,
  }: {
    serviceRecordCount: number;
    scrollY: SharedValue<number>;
    onRefresh: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { taskNo, detail, taskManager, profile, storeConfiguration } =
      useTaskContext();
    const [state] = useBehaviorSubject(taskManager.state$);

    const basicInfo = detail.basicInfo;
    const extraInfo = useTaskExtraInfo();
    const canceled = basicInfo.status === InspectionTaskStatus.Cancelled;

    const subscribed =
      extraInfo?.observes != null && extraInfo.observes.length > 0;

    const refreshRef = useRef(null);

    const modifiedRpx: PixelScaleFn = useCallback(
      (value, options) => {
        return rpx(value, options) * 0.83;
      },
      [rpx],
    );

    const tabletRightColumnContentType = () =>
      canceled
        ? TabletRightColumnContentType.Timeline
        : TabletRightColumnContentType.OrderDetail;

    const handleScroll: Exclude<ScrollViewProps['onScroll'], undefined> =
      useCallback(
        e => {
          'worklet';
          scrollY.value = e.nativeEvent.contentOffset.y;
        },
        [scrollY],
      );

    const preInspection = useStoreFeature(StoreFeature.PreInspection);
    const shouldShowPreInspectionInfo =
      kAlwaysShowPreInspectionInfo ||
      (!detail.basicInfo.hasChildOrders &&
        // if already started, always show it
        (detail.preInspection.status === CommonTaskStatus.InProgress ||
          // if already finished, always show it
          detail.preInspection.status === CommonTaskStatus.Finished ||
          // otherwise, show it if the module is enabled and feature not disabled
          (!storeConfiguration.isPreInspectionDisabled &&
            preInspection.enabled)));

    return (
      <HStack
        alignItems="stretch"
        css={`
          flex: 1;
          padding-top: ${rpx(15)}px;
        `}
      >
        <View
          css={`
            flex: 1;
          `}
        >
          <ScrollView
            waitFor={Platform.OS === 'android' ? refreshRef : undefined}
            scrollEventThrottle={1}
            onScroll={handleScroll}
            showsVerticalScrollIndicator={Platform.OS === 'web'}
            contentContainerStyle={{
              paddingTop: 0,
              paddingLeft: rpx(20),
              paddingRight: rpx(10),
              paddingBottom: rpx(80),
            }}
            refreshControl={
              Platform.OS !== 'web' ? (
                <RefreshControl
                  ref={refreshRef}
                  refreshing={Boolean(
                    state.status === 'loading' && state.refreshing,
                  )}
                  onRefresh={onRefresh}
                />
              ) : undefined
            }
          >
            <WiredTaskDetailPageOrderSummary
              hideExtendedProps
              serviceRecordCount={serviceRecordCount}
            />

            {!canceled && (
              <TaskDetailMenu serviceRecordCount={serviceRecordCount} />
            )}

            <WiredRemarkSection />

            {!canceled && shouldShowPreInspectionInfo && (
              <WiredPreInspectionInfo />
            )}

            {!canceled && <WiredFaultInspectionInfo />}

            {!canceled && !detail.basicInfo.hasChildOrders && (
              <WiredInspectionInfo />
            )}

            {!canceled && !detail.basicInfo.hasChildOrders && (
              <WiredConstructionInfo />
            )}

            {!canceled && detail.basicInfo.hasChildOrders && <ChildTaskList />}

            {!canceled && <WiredDeliveryCheckInfo />}

            {canceled && (
              <TaskDetailSection title="订单详情">
                <ResponsiveContextProvider scaled={modifiedRpx}>
                  <OrderDetailContent
                    hideBasicInfoTitle
                    hideVIN
                    hideMileage
                    hideTags
                    hideRemark
                    hideServiceAgent
                    hideQrcode={subscribed}
                  />
                </ResponsiveContextProvider>
              </TaskDetailSection>
            )}
          </ScrollView>

          <WiredTaskDetailFinishAction scrollY={scrollY} />
        </View>

        <TaskDetailSection
          title={
            tabletRightColumnContentType() ===
            TabletRightColumnContentType.Timeline
              ? '服务进度'
              : '订单详情'
          }
          bodyStyle={{
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            paddingBottom: rpx(15),
            flex: 1,
          }}
          containerStyle={{
            width: '40%',
            paddingLeft: rpx(10),
            paddingTop: 0,
            paddingRight: rpx(20),
            alignSelf: 'stretch',
          }}
          css={`
            flex: 1;
          `}
        >
          <LayoutProviderView
            css={`
              flex: 1;
            `}
          >
            {tabletRightColumnContentType() ===
            TabletRightColumnContentType.Timeline ? (
              <TaskTimeline
                taskNo={taskNo}
                readonly={canceled}
                isIndependentDeliveryCheckEnabled={
                  profile.store.isIndependentDeliveryCheckEnabled
                }
              />
            ) : (
              <ScrollView>
                <ResponsiveContextProvider scaled={modifiedRpx}>
                  <OrderDetailContent
                    hideBasicInfoTitle
                    hideVIN
                    hideMileage
                    hideTags
                    hideRemark
                    hideServiceAgent
                    hideQrcode={subscribed}
                  />
                </ResponsiveContextProvider>
              </ScrollView>
            )}
          </LayoutProviderView>
        </TaskDetailSection>
      </HStack>
    );
  },
);
